import { useCallback, useEffect, useState } from 'react';

const MOBILE_BREAKPOINT = 768; // matches Tailwind `md` and useIsMobile breakpoint
const MARGIN = 16;

export type PanelPosition =
  | { type: 'absolute'; anchor: 'left' | 'right' }
  | { type: 'fixed'; left: number; width: number; top?: number; bottom?: number };

export interface UsePanelPositionOptions {
  /** Max panel width on mobile (default: 360) */
  maxWidth?: number;
  /** Panel height used to prevent clipping at bottom of viewport (default: 400) */
  panelHeight?: number;
  /** Desktop: if panel would overflow right edge by this width, anchor right instead. 0 = always left. */
  desktopMinWidth?: number;
  /**
   * 'responsive' (default): absolute below the button on desktop, fixed on mobile.
   * 'viewport': fixed on every screen size and opens upward when there's more room above —
   * for triggers inside scrolling or clipped containers such as modals.
   */
  strategy?: 'responsive' | 'viewport';
}

export function usePanelPosition(
  buttonRef: React.RefObject<HTMLDivElement>,
  isOpen: boolean,
  options: UsePanelPositionOptions = {},
): PanelPosition {
  const {
    maxWidth = 360,
    panelHeight = 400,
    desktopMinWidth = 0,
    strategy = 'responsive',
  } = options;
  const [pos, setPos] = useState<PanelPosition>({ type: 'absolute', anchor: 'left' });

  const calculate = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (strategy === 'viewport' && vw >= MOBILE_BREAKPOINT) {
      const width = Math.min(Math.max(rect.width, desktopMinWidth), vw - MARGIN * 2);
      const left = Math.max(MARGIN, Math.min(rect.left, vw - width - MARGIN));
      const spaceBelow = vh - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;
      if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
        setPos({ type: 'fixed', left, width, top: rect.bottom + 4 });
      } else {
        setPos({ type: 'fixed', left, width, bottom: vh - rect.top + 4 });
      }
    } else if (vw < MOBILE_BREAKPOINT) {
      const width = Math.min(vw - MARGIN * 2, maxWidth);
      const top = Math.max(0, Math.min(rect.bottom + 4, vh - panelHeight));
      setPos({ type: 'fixed', left: MARGIN, top, width });
    } else {
      const anchor = desktopMinWidth > 0 && rect.left + desktopMinWidth > vw ? 'right' : 'left';
      setPos({ type: 'absolute', anchor });
    }
  }, [buttonRef, maxWidth, panelHeight, desktopMinWidth, strategy]);

  useEffect(() => {
    if (!isOpen) return;
    calculate();
    window.addEventListener('resize', calculate);
    // A fixed panel must follow its trigger when an ancestor (e.g. a modal body) scrolls.
    if (strategy === 'viewport') window.addEventListener('scroll', calculate, true);
    return () => {
      window.removeEventListener('resize', calculate);
      window.removeEventListener('scroll', calculate, true);
    };
  }, [isOpen, calculate, strategy]);

  return pos;
}
